import * as React from 'react';
import { cn } from '../../utils/cn';

type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
type HeadingSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
type HeadingWeight = 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold';
type HeadingColor = 'default' | 'primary' | 'secondary' | 'muted' | 'error';
type HeadingAlign = 'left' | 'center' | 'right' | 'justify';

export interface HeadingProps {
  as?: HeadingLevel;
  size?: HeadingSize;
  weight?: HeadingWeight;
  color?: HeadingColor;
  align?: HeadingAlign;
  truncate?: boolean;
  clamp?: number;
  gradient?: boolean;
  id?: string;
  children: React.ReactNode;
  className?: string;
}

const sizeClasses: Record<HeadingSize, string> = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-base md:text-lg',
  lg: 'text-lg md:text-xl lg:text-2xl',
  xl: 'text-xl md:text-2xl lg:text-3xl',
  '2xl': 'text-2xl md:text-3xl lg:text-4xl',
  '3xl': 'text-3xl md:text-4xl lg:text-5xl',
  '4xl': 'text-4xl md:text-5xl lg:text-6xl',
  '5xl': 'text-5xl md:text-6xl lg:text-7xl',
};

const headingSizeMap: Record<
  HeadingLevel,
  { default: HeadingSize; md: HeadingSize; lg: HeadingSize }
> = {
  h1: { default: 'xl', md: '2xl', lg: '2xl' },
  h2: { default: 'lg', md: 'xl', lg: 'xl' },
  h3: { default: 'md', md: 'lg', lg: 'lg' },
  h4: { default: 'sm', md: 'md', lg: 'md' },
  h5: { default: 'xs', md: 'sm', lg: 'sm' },
  h6: { default: 'xs', md: 'xs', lg: 'xs' },
};

const weightClasses: Record<HeadingWeight, string> = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
  extrabold: 'font-extrabold',
};

const colorClasses: Record<HeadingColor, string> = {
  default: 'text-base-content',
  primary: 'text-primary',
  secondary: 'text-secondary',
  muted: 'text-base-content/70',
  error: 'text-error',
};

const alignClasses: Record<HeadingAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify',
};

export const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  (
    {
      as: Component = 'h2',
      size,
      weight = 'bold',
      color = 'default',
      align,
      truncate = false,
      clamp,
      gradient = false,
      id,
      children,
      className,
    },
    ref
  ) => {
    // Use automatic sizing based on heading level if size not provided
    const autoSize = size || sizeClasses[headingSizeMap[Component].default];
    const sizeClass = size ? sizeClasses[size] : autoSize;

    const clampClass = clamp ? `line-clamp-${clamp}` : truncate ? 'truncate' : '';

    const gradientClass = gradient
      ? 'bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent'
      : '';

    return (
      <Component
        ref={ref}
        id={id}
        className={cn(
          sizeClass,
          weightClasses[weight],
          !gradient && colorClasses[color],
          align && alignClasses[align],
          clampClass,
          gradientClass,
          'tracking-tight',
          className
        )}
      >
        {children}
      </Component>
    );
  }
);

Heading.displayName = 'Heading';
